import { useEffect, useState } from "react";
import SiteLayout from "@/components/site/SiteLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

// Authentic Sarandë photos — sourced from Wikipedia / Wikimedia Commons (CC)
const SARANDE_PHOTOS = [
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7e/CIty_of_Saranda_Albania_2016.jpg/1280px-CIty_of_Saranda_Albania_2016.jpg", alt: "View of Sarandë" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Kalaja_e_Lekursit_01.JPG/1280px-Kalaja_e_Lekursit_01.JPG", alt: "Lëkurësi Castle overlooking Sarandë" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fc/Saranda_Albania_Promenade_2016.jpg/1280px-Saranda_Albania_Promenade_2016.jpg", alt: "Promenade of Sarandë" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Ksamill-1.jpg/1280px-Ksamill-1.jpg", alt: "Islets of Ksamil" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/42/Vivari_Channel_within_the_Butrint_National_Park_in_Albania.jpg/1280px-Vivari_Channel_within_the_Butrint_National_Park_in_Albania.jpg", alt: "Vivari Channel within Butrint National Park" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/15/Blue_Eye_Albania_2016_Syri_i_kalter.jpg/1280px-Blue_Eye_Albania_2016_Syri_i_kalter.jpg", alt: "The Blue Eye Spring (Syri i Kaltër)" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/be/Saranda_Albania_2016.jpg/1280px-Saranda_Albania_2016.jpg", alt: "Promenade and beach of Sarandë" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6f/Windstar_star_breeze_cruise_ship_in_Saranda_Albania_2016.jpg/1280px-Windstar_star_breeze_cruise_ship_in_Saranda_Albania_2016.jpg", alt: "Star Breeze cruise ship in the Port of Sarandë" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4f/Mousque_Sarand%C3%AB_%287912538044%29.jpg/1280px-Mousque_Sarand%C3%AB_%287912538044%29.jpg", alt: "Mosque of Sarandë" },
];

const HERO = "https://upload.wikimedia.org/wikipedia/commons/thumb/a/af/Panoramic_view_of_Saranda_from_L%C3%ABkur%C3%ABsi_Castle.jpg/1920px-Panoramic_view_of_Saranda_from_L%C3%ABkur%C3%ABsi_Castle.jpg";

export default function AboutPage() {
  const { t, trSetting } = useI18n();
  const [s, setS] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  return (
    <SiteLayout>
      <Seo title={`${t("nav.about")} · Berdhill Rooms`} description={trSetting(s, "about_text")} />
      <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12 py-16">
        <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{t("nav.about")}</div>
        <h1 className="font-display text-5xl md:text-6xl mt-3 leading-tight max-w-3xl">{t("home.intro_title")}</h1>

        <figure className="mt-12">
          <div className="aspect-[21/9] overflow-hidden rounded-sm">
            <img src={HERO} alt="Panoramic view of Sarandë from Lëkurësi Castle" className="w-full h-full object-cover" />
          </div>
          <figcaption className="text-xs text-brand-muted/80 mt-2">Panoramic view of Sarandë from Lëkurësi Castle.</figcaption>
        </figure>

        <p className="mt-12 text-lg leading-relaxed text-brand-text/85 max-w-3xl whitespace-pre-line">{trSetting(s, "about_text")}</p>

        {/* About Sarandë — facts sourced from Wikipedia (CC BY-SA) */}
        <section className="mt-20" data-testid="about-sarande-section">
          <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">{t("home.location")}</div>
          <h2 className="font-display text-3xl md:text-4xl mt-3">About Sarandë</h2>

          <div className="mt-8 grid md:grid-cols-2 gap-10 items-start">
            <div className="space-y-5 text-brand-text/85 leading-relaxed">
              <p>
                <strong>Sarandë</strong> (Albanian: <em>Saranda</em>; Greek: Άγιοι Σαράντα)
                is a coastal city in southern Albania, the seat of Sarandë Municipality in
                Vlorë County. It sits on a horseshoe-shaped bay on the Ionian Sea, opposite
                the Greek island of Corfu.
              </p>
              <p>
                In antiquity the city was known as <strong>Onchesmus</strong>, a port-town of
                Chaonia in ancient Epirus. It owes its modern name to the nearby Byzantine
                <em> Monastery of the Forty Saints</em> (<em>Agioi Saranda</em>), recorded
                from the High Middle Ages.
              </p>
              <p>
                Today Sarandë is the unofficial capital of the <strong>Albanian Riviera</strong>
                and one of the country's fastest-growing tourist destinations, drawing
                visitors by ferry from Corfu and by cruise ship to its expanding port.
              </p>
            </div>

            <div className="rounded-md border border-brand-border bg-white p-6 text-sm">
              <div className="text-xs tracking-[0.2em] uppercase text-brand-muted mb-4">Quick facts</div>
              <dl className="space-y-3">
                <div className="flex justify-between gap-4"><dt className="text-brand-muted">Country</dt><dd>Albania</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-brand-muted">County</dt><dd>Vlorë</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-brand-muted">Region</dt><dd>Albanian Riviera</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-brand-muted">Coordinates</dt><dd>39.875° N, 20.010° E</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-brand-muted">Population (2023)</dt><dd>≈ 19,882</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-brand-muted">Climate</dt><dd>Mediterranean (Csa)</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-brand-muted">Sunny days / yr</dt><dd>300+</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-brand-muted">Time zone</dt><dd>CET / CEST</dd></div>
              </dl>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-display text-xl mb-2">Butrint</h3>
              <p className="text-sm text-brand-text/80 leading-relaxed">
                A UNESCO World Heritage site just south of Sarandë, Butrint preserves layers
                of Greek, Roman, Byzantine, Venetian and Ottoman ruins in a tranquil lagoon
                setting inside the Butrint National Park.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl mb-2">The Blue Eye</h3>
              <p className="text-sm text-brand-text/80 leading-relaxed">
                <em>Syri i Kaltër</em> is a natural spring of impossibly clear, deep-blue
                water more than 50 m deep — an easy day-trip inland from Sarandë through
                olive groves and pine forest.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl mb-2">Ksamil</h3>
              <p className="text-sm text-brand-text/80 leading-relaxed">
                A short drive south, Ksamil is famed for its white-sand beaches and four
                small offshore islets in turquoise water — the most photographed stretch
                of the entire Albanian Riviera.
              </p>
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="mt-16" data-testid="about-gallery">
          <h2 className="font-display text-3xl md:text-4xl">A glimpse of Sarandë</h2>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {SARANDE_PHOTOS.map((p, i) => (
              <figure key={i} className="group" data-testid={`about-gallery-img-${i}`}>
                <div className="aspect-[4/3] overflow-hidden rounded-md">
                  <img src={p.src} alt={p.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <figcaption className="text-[11px] text-brand-muted mt-1.5">{p.alt}</figcaption>
              </figure>
            ))}
          </div>
          <p className="text-[11px] text-brand-muted/70 mt-6">
            Photos & facts adapted from <a href="https://en.wikipedia.org/wiki/Sarand%C3%AB" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-primary">Wikipedia — Sarandë</a>, available under CC BY-SA. Individual images sourced from Wikimedia Commons.
          </p>
        </section>
      </div>
    </SiteLayout>
  );
}
